import { Dashboard } from "@/models/dashboard/Dashboard";

interface PublishContext {
  /** Whether the current user may manage this tenant's organization dashboards. */
  canEditOrgDashboards: boolean;
}

interface SharingContext extends PublishContext {
  /** Whether the current tenant has sub-tenants to release dashboards to. */
  isParentTenant: boolean;
}

/**
 * Whether this dashboard belongs to the current tenant and the user may manage it (LAR-93).
 * Personal dashboards have no audience to manage, and a dashboard inherited from a parent tenant
 * belongs to that parent.
 */
const isManageableOrganizationDashboard = (
  dashboard: Dashboard | undefined,
  { canEditOrgDashboards }: PublishContext
): dashboard is Dashboard =>
  !!dashboard &&
  dashboard.isOrganizationDashboard &&
  !dashboard.isFromParentTenant &&
  canEditOrgDashboards;

/**
 * Whether the publish controls belong on this dashboard (LAR-93).
 *
 * Organization dashboards are drafted privately: until they are published, only the people who can
 * edit them see them at all. Publishing is therefore an action on every organization dashboard the
 * tenant owns, published or not — the control flips between publishing and reverting to a draft.
 */
export const canPublishDashboard = (
  dashboard: Dashboard | undefined,
  context: PublishContext
): boolean => isManageableOrganizationDashboard(dashboard, context);

/**
 * Whether the release controls (share with sub-tenants, and withdraw again) belong on this
 * dashboard (LAR-93).
 *
 * Releasing is the second step after publishing: a dashboard the tenant's own users cannot see is
 * never handed to its sub-tenants, and a tenant without sub-tenants has nobody to release to. An
 * already-released dashboard keeps the control regardless, so a tenant that has since given up its
 * sub-tenants can still withdraw what it shared.
 */
export const canManageSubTenantSharing = (
  dashboard: Dashboard | undefined,
  context: SharingContext
): boolean =>
  isManageableOrganizationDashboard(dashboard, context) &&
  dashboard.isPublished &&
  (context.isParentTenant || dashboard.isSharedWithSubTenants);
